import {
  EMPTY_MAKEUP_REQUEST_FILTERS,
  type MakeupRequestFiltersState,
  makeupRequestFiltersEqual,
  parseMakeupRequestFiltersFromSearchParams,
} from "@/features/makeups/shared/makeupRequestFilters";
import {
  type MakeupQueueView,
  MAKEUP_QUEUE_VIEW_REVIEW,
  parseMakeupQueueView,
} from "@/lib/makeups/makeup-queue-view";

export type MakeupRequestsPageUrlState = {
  view: MakeupQueueView;
  quickFirstName: string;
  quickLastName: string;
  filters: MakeupRequestFiltersState;
  page: number;
  lastSelectedId?: string;
};

export const EMPTY_MAKEUP_REQUESTS_PAGE_URL_STATE: MakeupRequestsPageUrlState =
  {
    view: MAKEUP_QUEUE_VIEW_REVIEW,
    quickFirstName: "",
    quickLastName: "",
    filters: EMPTY_MAKEUP_REQUEST_FILTERS,
    page: 1,
  };

function parsePage(value: string | null): number {
  if (value == null || value.trim() === "") return 1;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

function parseScrollPagePart(searchParams: URLSearchParams): {
  page: number;
  lastSelectedId?: string;
} {
  const page = parsePage(searchParams.get("page"));
  if (page <= 1) return { page: 1 };
  const lastSelectedId = searchParams.get("lastSelectedId")?.trim();
  if (!lastSelectedId) return { page: 1 };
  return { page, lastSelectedId };
}

export function parseMakeupRequestsPageParams(
  searchParams: URLSearchParams,
): MakeupRequestsPageUrlState {
  const { page, lastSelectedId } = parseScrollPagePart(searchParams);
  return {
    view: parseMakeupQueueView(searchParams),
    quickFirstName: searchParams.get("q")?.trim() ?? "",
    quickLastName: searchParams.get("lq")?.trim() ?? "",
    filters: parseMakeupRequestFiltersFromSearchParams(searchParams),
    page,
    lastSelectedId,
  };
}

export function buildMakeupRequestsPageQuery(
  view: MakeupQueueView,
  quickFirstName: string,
  quickLastName: string,
  filters: MakeupRequestFiltersState,
  page: number,
  lastSelectedId?: string,
): string {
  const params = new URLSearchParams();

  params.set("view", view);

  if (quickFirstName.trim()) {
    params.set("q", quickFirstName.trim());
  }
  if (quickLastName.trim()) {
    params.set("lq", quickLastName.trim());
  }

  if (filters.firstName.trim()) {
    params.set("firstName", filters.firstName.trim());
  }
  if (filters.lastName.trim()) {
    params.set("lastName", filters.lastName.trim());
  }
  if (filters.status !== EMPTY_MAKEUP_REQUEST_FILTERS.status) {
    params.set("status", filters.status);
  }
  if (filters.studentId != null) {
    params.set("studentId", String(filters.studentId));
  }
  if (filters.sourceClassId != null) {
    params.set("sourceClassId", String(filters.sourceClassId));
  }
  if (filters.makeupClassId != null) {
    params.set("makeupClassId", String(filters.makeupClassId));
  }
  if (filters.locationId != null) {
    params.set("locationId", String(filters.locationId));
  }
  if (filters.fromDate.trim()) {
    params.set("fromDate", filters.fromDate.trim());
  }
  if (filters.toDate.trim()) {
    params.set("toDate", filters.toDate.trim());
  }
  if (page > 1) {
    params.set("page", String(page));
  }
  if (page > 1 && lastSelectedId) {
    params.set("lastSelectedId", lastSelectedId);
  }

  const query = params.toString();
  return query ? `?${query}` : "";
}

export function makeupRequestsPageUrlMatchesState(
  searchParams: URLSearchParams,
  view: MakeupQueueView,
  quickFirstName: string,
  quickLastName: string,
  filters: MakeupRequestFiltersState,
  page: number,
  lastSelectedId: string | undefined,
): boolean {
  const fromUrl = parseMakeupRequestsPageParams(searchParams);
  if (fromUrl.view !== view) return false;
  if (fromUrl.quickFirstName !== quickFirstName.trim()) return false;
  if (fromUrl.quickLastName !== quickLastName.trim()) return false;
  if (!makeupRequestFiltersEqual(fromUrl.filters, filters)) return false;
  if (fromUrl.page !== page) return false;
  if (page <= 1) return true;
  return fromUrl.lastSelectedId === lastSelectedId;
}

export function shouldDeferMakeupRequestsPageUrlSync(
  searchParams: URLSearchParams,
  page: number,
  cursorAfterPage: Record<number, string>,
): boolean {
  if (page <= 1) return false;

  const fromUrl = parseMakeupRequestsPageParams(searchParams);
  if (fromUrl.page > page && cursorAfterPage[page] == null) {
    return true;
  }
  if (
    fromUrl.page > 1 &&
    fromUrl.lastSelectedId &&
    cursorAfterPage[fromUrl.page - 1] !== fromUrl.lastSelectedId
  ) {
    return true;
  }
  if (page > 1 && cursorAfterPage[page - 1] == null) {
    return true;
  }
  return false;
}
